'use client';
import { useTestConfig } from '@/context/TestConfigContext';
import { addLeaderboardEntry, getPersonalBest, savePersonalBest, type TestResult } from '@/data/localScores';
import { getShuffledWords } from '@/data/wordLists';
import { useCallback, useEffect, useRef, useState } from 'react';
import LiveStats from './LiveStats';
import ResultPanel from './ResultPanel';
import StageControls from './StageControls';
import TypingInput from './TypingInput';
import WordDisplay from './WordDisplay';

type TestState = 'idle' | 'active' | 'completed';
const WORD_COUNT = 400;

export default function TypingTestClient() {
  const { language, duration } = useTestConfig();

  const [testState, setTestState] = useState<TestState>('idle');
  const [words, setWords] = useState<string[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentInput, setCurrentInput] = useState('');
  const [wordStatuses, setWordStatuses] = useState<('correct' | 'incorrect' | 'pending')[]>([]);
  const [timeLeft, setTimeLeft] = useState(60);
  const [keystrokes, setKeystrokes] = useState(0);
  const [liveWpm, setLiveWpm] = useState(0);
  const [liveAccuracy, setLiveAccuracy] = useState(100);

  const [result, setResult] = useState<TestResult | null>(null);
  const [isNewBest, setIsNewBest] = useState(false);
  const [prevBest, setPrevBest] = useState<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  const [wordsHeight, setWordsHeight] = useState(300);

  // Authoritative counters (refs avoid stale-closure bugs in the timer)
  const cwRef = useRef(0);   // correct words
  const wwRef = useRef(0);   // wrong words
  const ksRef = useRef(0);   // keystrokes
  const ckRef = useRef(0);   // correct keystrokes
  const ccRef = useRef(0);   // correct characters (for CPM)
  const prevInputRef = useRef('');
  const wordsRef = useRef<string[]>([]);
  const idxRef = useRef(0);
  const timelineRef = useRef<number[]>([]);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const typingAreaRef = useRef<HTMLDivElement>(null);

  // Programmatic focus during initial page load is flaky (browsers restrict it
  // before a user gesture), so retry a few frames before giving up. preventScroll
  // stops the browser from jumping the page when focus lands.
  const focusInput = useCallback(() => {
    let attempt = 0;
    const tryFocus = () => {
      const el = inputRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      if (document.activeElement === el) {
        // Repair the focus flag from the *actual* focus state. The onFocus event
        // can be missed when focus lands programmatically right as the user starts
        // typing (a mount/refresh race), which otherwise leaves isFocused stuck
        // false and pops the "click to focus" overlay over text that is in fact
        // being typed correctly.
        setIsFocused(true);
      } else if (attempt++ < 6) {
        setTimeout(() => requestAnimationFrame(tryFocus), 50);
      }
    };
    requestAnimationFrame(tryFocus);
  }, []);

  const resetTest = useCallback(
    (newWords: string[]) => {
      if (timerRef.current) clearInterval(timerRef.current);
      cwRef.current = 0; wwRef.current = 0; ksRef.current = 0; ckRef.current = 0; ccRef.current = 0;
      prevInputRef.current = '';
      idxRef.current = 0;
      timelineRef.current = [];
      wordsRef.current = newWords;
      startTimeRef.current = 0;

      setWords(newWords);
      setWordStatuses(new Array(newWords.length).fill('pending'));
      setCurrentWordIndex(0);
      setCurrentInput('');
      setTimeLeft(duration);
      setKeystrokes(0);
      setLiveWpm(0);
      setLiveAccuracy(100);
      setResult(null);
      setIsNewBest(false);
      setPrevBest(null);
      setTestState('idle');
      focusInput();
    },
    [duration, focusInput]
  );

  const newTest = useCallback(() => resetTest(getShuffledWords(language, WORD_COUNT)), [language, resetTest]);
  const retrySame = useCallback(() => resetTest(wordsRef.current.length ? wordsRef.current : getShuffledWords(language, WORD_COUNT)), [language, resetTest]);

  // New word set whenever language or duration changes
  useEffect(() => {
    resetTest(getShuffledWords(language, WORD_COUNT));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, duration]);

  const elapsedMinutes = () => Math.max((Date.now() - startTimeRef.current) / 60000, 1 / 60);

  const finishTest = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    const minutes = elapsedMinutes();
    const wpm = Math.round(cwRef.current / minutes);
    const cpm = Math.round(ccRef.current / minutes);
    const accuracy = ksRef.current > 0 ? Math.round((ckRef.current / ksRef.current) * 1000) / 10 : 100;
    const errorRate = ksRef.current > 0 ? Math.round(((ksRef.current - ckRef.current) / ksRef.current) * 1000) / 10 : 0;

    const res: TestResult = {
      wpm, cpm, accuracy,
      correctWords: cwRef.current,
      wrongWords: wwRef.current,
      keystrokes: ksRef.current,
      errorRate,
      duration,
      language,
      timestamp: new Date().toISOString(),
      wpmTimeline: timelineRef.current,
    };

    const existing = getPersonalBest(language, duration);
    setPrevBest(existing?.wpm ?? null);
    const newBest = savePersonalBest(language, duration, res);
    addLeaderboardEntry({ wpm, accuracy, language, duration, timestamp: res.timestamp });

    setIsNewBest(newBest);
    setResult(res);
    setTestState('completed');
  }, [duration, language]);

  // Countdown timer
  useEffect(() => {
    if (testState !== 'active') return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const wpm = Math.round(cwRef.current / elapsedMinutes());
        timelineRef.current = [...timelineRef.current, wpm];
        setLiveWpm(wpm);
        if (prev <= 1) {
          finishTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [testState, finishTest]);

  // Tab / Esc => fresh test. Any other key => keep the hidden input focused so
  // the user can just start typing without clicking first (monkeytype behaviour).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Tab' || e.key === 'Escape') {
        e.preventDefault();
        newTest();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.activeElement !== inputRef.current) focusInput();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [newTest, focusInput]);

  // Re-grab focus when the tab/window becomes active again (covers the case
  // where focus silently failed on load or was stolen by another element).
  useEffect(() => {
    if (testState === 'completed') return;
    const refocus = () => {
      if (document.activeElement !== inputRef.current) focusInput();
    };
    window.addEventListener('focus', refocus);
    document.addEventListener('visibilitychange', refocus);
    return () => {
      window.removeEventListener('focus', refocus);
      document.removeEventListener('visibilitychange', refocus);
    };
  }, [testState, focusInput]);

  // ── AdSense focus-steal guard ────────────────────────────────────────────
  // AdSense (and other) ad iframes grab keyboard focus the moment they finish
  // loading — typically a beat after a hard refresh, right as the user starts
  // typing. Because the ad is a *cross-origin* iframe, once it holds focus the
  // user's keystrokes flow into the ad, NOT our input: the parent window never
  // sees those keydowns, so the "press any key to refocus" handler can't rescue
  // it. The words blur, the overlay pops, and typing does nothing until the user
  // clicks. The reclaim therefore has to be proactive.
  //
  // isAdSteal: focus is held by an <iframe> rather than a real interactive
  // control (button/link/select) the user meant to click.
  const isAdSteal = useCallback(() => {
    const el = document.activeElement;
    return !!el && el.tagName === 'IFRAME';
  }, []);

  // Primary net — an EVENT-INDEPENDENT watchdog. When an ad script *inside* a
  // cross-origin iframe grabs focus, the parent document often receives NO
  // `focusin`/`focus`/`blur` event at all (browsers don't surface focus moves
  // that originate inside a cross-origin child) — so any event-driven reclaim
  // can silently miss it, which is why the earlier focusin/onBlur nets weren't
  // enough on the live site. Instead we simply poll: if an <iframe> is holding
  // focus during the test, the user can't be interacting with it (there are no
  // legitimate iframes in the typing UI), so reclaim. Safe because it only ever
  // fires on IFRAME — never on a real click on a button/select/link.
  useEffect(() => {
    if (testState === 'completed') return;
    const reclaimFromIframe = () => {
      const el = document.activeElement as HTMLElement | null;
      if (el && el.tagName === 'IFRAME' && el !== inputRef.current) {
        // Blur the ad's iframe first — pulling focus out of it directly is the
        // reliable move; a bare input.focus() can be ignored while a cross-origin
        // iframe holds focus. Then grab it back for the typing input.
        el.blur?.();
        focusInput();
      }
    };
    const id = setInterval(reclaimFromIframe, 250);
    // Also react immediately on the events that *do* fire, so the typical case
    // is fixed within a frame rather than waiting up to a poll interval.
    const onFocusIn = (e: FocusEvent) => {
      if ((e.target as HTMLElement | null)?.tagName === 'IFRAME') focusInput();
    };
    document.addEventListener('focusin', onFocusIn);
    window.addEventListener('blur', reclaimFromIframe);
    return () => {
      clearInterval(id);
      document.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('blur', reclaimFromIframe);
    };
  }, [testState, focusInput, isAdSteal]);

  // Suppress the mid-test blur overlay while a header config dropdown is open,
  // and refocus the typing input when it closes (unless the user clicked away).
  useEffect(() => {
    const onOpen = () => setConfigMenuOpen(true);
    const onClose = (e: Event) => {
      setConfigMenuOpen(false);
      if (testState === 'completed') return;
      const detail = (e as CustomEvent<{ refocus?: boolean; target?: EventTarget | null }>).detail;
      const clickedTypingArea =
        detail?.target instanceof Node && typingAreaRef.current?.contains(detail.target);
      if (detail?.refocus || clickedTypingArea) focusInput();
    };
    document.addEventListener('typeswift-config-open', onOpen);
    document.addEventListener('typeswift-config-close', onClose);
    return () => {
      document.removeEventListener('typeswift-config-open', onOpen);
      document.removeEventListener('typeswift-config-close', onClose);
    };
  }, [testState, focusInput]);

  const isTestConfigControl = useCallback((el: EventTarget | null) => {
    return el instanceof Element && !!el.closest('[data-test-config-controls]');
  }, []);

  // Robust blur trigger: relying only on the input's `onBlur` is unreliable
  // (the event can silently not fire — e.g. focus was never cleanly on the
  // input, or after a dropdown interaction), which left the words un-blurred
  // after clicking away. So during an active test we also watch document-level
  // pointerdowns: a click outside the typing area (and not on a header config
  // control, which manages its own focus) blurs the words directly.
  useEffect(() => {
    if (testState !== 'active') return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target;
      if (typingAreaRef.current && target instanceof Node && typingAreaRef.current.contains(target)) return;
      if (isTestConfigControl(target)) return;
      setIsFocused(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [testState, isTestConfigControl]);

  // Backup net + overlay control: when the input itself blurs, decide (after a
  // frame, so activeElement has settled) whether it was an ad steal or a genuine
  // click-away. Reclaim on an ad steal without ever flagging the test as blurred,
  // so the "click to focus" overlay never flashes over the words.
  const handleInputBlur = useCallback(() => {
    if (testState === 'completed') {
      setIsFocused(false);
      return;
    }
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active === inputRef.current) return; // focus already back — nothing to do
      if (isAdSteal()) {
        focusInput();
        return;
      }
      setIsFocused(false); // genuine click-away (button, body, config control…)
    });
  }, [testState, focusInput, isAdSteal]);

  const handleInput = useCallback(
    (value: string) => {
      if (testState === 'completed') return;
      // An onChange can only fire while the input holds focus, so this is the
      // authoritative signal that we are focused — set it directly rather than
      // trusting the onFocus event, which can be missed in a mount/refresh race
      // and leave the "click to focus" overlay stuck over text being typed.
      setIsFocused(true);
      if (testState === 'idle') {
        setTestState('active');
        startTimeRef.current = Date.now();
      }

      const idx = idxRef.current;
      const expected = wordsRef.current[idx] || '';

      // Submit word on space
      if (value.endsWith(' ')) {
        const typed = value.slice(0, -1);
        if (typed.length === 0) return; // ignore leading spaces
        const correct = typed === expected;

        ksRef.current += 1; // the space
        if (correct) {
          ckRef.current += 1;
          cwRef.current += 1;
          ccRef.current += expected.length + 1;
        } else {
          wwRef.current += 1;
        }

        setWordStatuses((prev) => {
          const next = [...prev];
          next[idx] = correct ? 'correct' : 'incorrect';
          return next;
        });
        idxRef.current = idx + 1;
        setCurrentWordIndex(idx + 1);
        setCurrentInput('');
        prevInputRef.current = '';
      } else {
        // Count a keystroke when a character was added (ignore backspaces)
        if (value.length > prevInputRef.current.length) {
          const pos = value.length - 1;
          ksRef.current += 1;
          if (value[pos] === expected[pos]) ckRef.current += 1;
        }
        prevInputRef.current = value;
        setCurrentInput(value);
      }

      // Live metrics
      if (startTimeRef.current > 0) {
        const minutes = elapsedMinutes();
        setLiveWpm(Math.round(cwRef.current / minutes));
        setLiveAccuracy(ksRef.current > 0 ? Math.round((ckRef.current / ksRef.current) * 1000) / 10 : 100);
      }
      setKeystrokes(ksRef.current);
    },
    [testState]
  );

  const progress = testState === 'idle' ? 0 : ((duration - timeLeft) / duration) * 100;
  const isLowTime = timeLeft <= 10 && testState === 'active';

  // Drag-to-resize the words box via the corner grip. Uses pointer capture so
  // the drag keeps tracking even if the cursor leaves the small handle.
  const startResize = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const startH = wordsHeight;
    const maxH = Math.round(window.innerHeight * 0.7);
    const onMove = (ev: PointerEvent) => {
      const next = Math.min(Math.max(startH + (ev.clientY - startY), 160), maxH);
      setWordsHeight(next);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.userSelect = '';
      focusInput();
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [wordsHeight, focusInput]);

  return (
    <div className="flex flex-col gap-5 max-w-4xl mx-auto">
      {testState === 'completed' && result ? (
        <ResultPanel
          result={result}
          isNewBest={isNewBest}
          prevBest={prevBest}
          onRetry={retrySame}
          onNewTest={newTest}
        />
      ) : (
        <div className="type-stage">
          {/* Head: controls on the left, live metrics on the right */}
          <div className="type-stage-head">
            <StageControls onRestart={newTest} />
            <LiveStats
              wpm={liveWpm}
              accuracy={liveAccuracy}
              timeLeft={timeLeft}
              keystrokes={keystrokes}
              testState={testState}
            />
          </div>

          {/* Progress rail */}
          <div className="type-stage-progress">
            <div
              className="type-stage-progress-fill"
              style={{
                width: `${progress}%`,
                ...(isLowTime ? { background: 'var(--incorrect)' } : {}),
              }}
            />
          </div>

          {/* Body: the words */}
          <div
            ref={typingAreaRef}
            className="type-stage-body cursor-text"
            onMouseDown={() => {
              if (testState === 'completed') return;
              // Optimistically clear the blur so the words sharpen instantly,
              // then land real focus (retried across frames by focusInput).
              setIsFocused(true);
              if (document.activeElement !== inputRef.current) focusInput();
            }}
          >
            <WordDisplay
              words={words}
              currentWordIndex={currentWordIndex}
              wordStatuses={wordStatuses}
              currentInput={currentInput}
              focused={isFocused}
              height={wordsHeight}
            />

            {/* Invisible full-cover input absorbs every keystroke (no visible box) */}
            <TypingInput
              ref={inputRef}
              value={currentInput}
              onChange={handleInput}
              disabled={testState === 'completed'}
              onFocus={() => setIsFocused(true)}
              onBlur={handleInputBlur}
            />

            {/* Drag handle to resize the words box */}
            <span
              className="typing-resize-grip"
              title="Drag to resize"
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize typing area"
              onPointerDown={startResize}
            />

            {/* Helper hint — only on the idle screen */}
            {testState === 'idle' && (
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-5">
                <span className="type-hint">
                  Just start typing — the timer starts on your first keystroke
                </span>
                <span className="type-hint">
                  <kbd>Tab</kbd> new test
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
